import { Cell, CellWithHierarchy } from '@pelckmans/elementis/components/table';
import { TeacherAssignment } from '../api/AssignmentTeacherApi';
import { useContext } from 'react';
import ModuleContext from '../../material/_contexts/ModuleContext';
import Avatar from '@pelckmans/elementis/components/avatar';
import AvatarImage from '@pelckmans/elementis/components/avatar/avatar-image';
import { useTranslation } from 'react-i18next';
import { formatAssignmentToDate } from '../../../utils/formatDate';
import { MaterialIcon } from '../../material/components/MaterialIcon';
import { EndDateCategory } from './AssignmentDivision';
import ShareIcon from '@pelckmans/elementis/icons/share';
import ResultsIcon from '@pelckmans/elementis/icons/results';
import { getFullHierarchyDescriptionOfNode } from '../../../utils/getNodeDescription';
import useNavigateResult from '../../../shared-components/elementis/hooks/useNavigateResult';
import TABS from '../../../shared-components/elementis/types/ResultsPageTabs';

const BASE = 'PAGES.ASSIGNMENTS.SECTION_PLANNED_AND_ONGOING.TABLE.BODY.ASSIGNMENTS';

type AssignmentProps = {
  assignment: TeacherAssignment;
  endDateCategory: EndDateCategory;
};

export default function Assignment({ assignment, endDateCategory }: AssignmentProps) {
  const { t } = useTranslation();
  const { module, subModules } = useContext(ModuleContext);
  const { createResultsUrl } = useNavigateResult(module);

  const assignmentModule = [module, ...subModules].find(m => m!.id === assignment.moduleId);

  const now = new Date();
  const from = new Date(assignment.from);

  const pillProminence =
    endDateCategory === 'TODAY' ? 'danger' : endDateCategory === 'THIS_WEEK' ? 'warning' : 'neutral';

  const period =
    from < now
      ? t(`${BASE}.PERIOD.TO`, { to: formatAssignmentToDate(assignment.to) })
      : t(`${BASE}.PERIOD.FROM_TO`, {
          from: formatAssignmentToDate(assignment.from),
          to: formatAssignmentToDate(assignment.to),
        });

  const locationIndicator = getFullHierarchyDescriptionOfNode(assignment.hierarchy, t);

  const { groupId, schoolId, userIds } = assignment.audience;

  return (
    <tr className="table-item">
      <CellWithHierarchy hierarchyProps={{ level: 1 }}>
        <Cell.Text
          title={assignment.name}
          startAdornment={<MaterialIcon materialType={assignment.materialType} contentType={assignment.contentType} />}
        />
      </CellWithHierarchy>
      <Cell>
        {assignmentModule && (
          <Cell.Text
            title={assignmentModule.name}
            subTitleBot={locationIndicator}
            startAdornment={
              <Avatar>
                <AvatarImage name={assignmentModule.name} src={assignmentModule.styling?.logo || ''} />
              </Avatar>
            }
          />
        )}
      </Cell>
      <Cell
        pillGroupProps={{
          'aria-label': t(`${BASE}.PERIOD.A11Y_LABEL`),
          role: 'group',
        }}
      >
        <Cell.Pill prominence={pillProminence}>{period}</Cell.Pill>
      </Cell>
      <Cell>
        <Cell.Text
          title={assignment.group || t(`${BASE}.ALL_GROUPS`)}
          subTitleBot={
            assignment.school.address
              ? `${assignment.school.name}, ${assignment.school.address.city}`
              : assignment.school.name
          }
        />
      </Cell>
      <Cell
        pillGroupProps={{
          'aria-label': t(`${BASE}.STUDENTS.A11Y_LABEL`),
          role: 'group',
        }}
      >
        <Cell.Pill>
          {assignment.audience.userIds
            ? String(assignment.audience.userIds.length)
            : t(`${BASE}.STUDENTS.ALL_STUDENTS`)}
        </Cell.Pill>
      </Cell>
      <Cell
        buttonGroupProps={{
          'aria-label': t(`${BASE}.ACTIONS.A11Y_LABEL`),
          role: 'group',
        }}
      >
        <Cell.Button
          startIcon={<ShareIcon data-testid="share-icon" />}
          prominence="neutral"
          aria-label={t(`${BASE}.ACTIONS.SHARE_BUTTON.A11Y_LABEL`)}
        />
        <Cell.Button
          as="link"
          startIcon={<ResultsIcon data-testid="results-icon" />}
          prominence="neutral"
          aria-label={t(`${BASE}.ACTIONS.SHARE_BUTTON.A11Y_LABEL`)}
          href={createResultsUrl({
            school: schoolId,
            module: assignment.moduleId,
            exercises: [{ id: assignment.materialId }],
            groups: groupId ? [groupId] : undefined,
            memberGroups: userIds && groupId ? userIds.map(userId => ({ member: userId, group: groupId })) : undefined,
            selectedTab: TABS.EXERCISE,
          })}
        />
      </Cell>
    </tr>
  );
}
